import React, { useCallback, useEffect, useState } from 'react';
import { onError, onSuccess, onWarning } from '../../components/shared/messages';
import { useTextResources } from '../../language';
import GridSearchTemplate from '../GridSearchTemplate';
import { onUpdateGridHandler } from '../../components/GridPanel/utility';
import { MenuActions } from '../../components/shared/constants';
import { Reasons, ReasonTypes } from '../../api/services/next';
import ReasonTypesDropDown from '../../components/shared/ReasonTypesDropdown';
import Checkbox from '../../components/shared/controlled/Checkbox';
import CheckboxWrapper from '../../components/shared/Checkbox';
import { useLoader } from "../../hooks/useLoader"
import TextInput from '../../components/shared/TextInput';
import { Form } from 'semantic-ui-react';
import './style.less';

const ReasonsSettingPage = () => {
  const text = useTextResources();
  const loader = useLoader();
  const [gridData, setGridData] = useState([]);
  const [deleted, setDeleted] = useState([])
  const [reasonTypesFromApi, setReasonTypesFromApi] = useState([]);

  const handleFieldChange = useCallback((row, fieldName, value) => {
    const id = row.Id || row.TempId;

    setGridData(prev => {
      const updated = [...prev];
      const toUpdate = updated.find(r => r.Id === id || r.TempId === id);

      if (toUpdate) {
        toUpdate[fieldName] = value;
      }

      return updated;
    });
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const reasonTypes = await ReasonTypes.GetReasonTypes();
        setReasonTypesFromApi(reasonTypes);
      } catch (error) {
        onError(error);
      }
    })();
  }, []);

  const onGridUpdate = (added, changed, deleted) => {
    console.log('Grid data changed:', changed);
    if (deleted?.length) {
      deleted = deleted.filter(r => !r.Id);
      setDeleted(prev => [...prev, ...gridData.filter((x, i) => deleted.includes(i))])
    }
    const updated = onUpdateGridHandler(gridData, added, changed, deleted);
    setGridData(updated);
  }

  const save = () => {
    (async () => {
      if (gridData?.length
      ) {
        if (gridData.some(r => !r.Name || r.Name.trim() === '')) {
          onWarning(text('OneOrMoreRowsHaveAMissingName'));
          return;
        }

        if (gridData.some(r => !r.ReasonTypeId)) {
          onWarning(text('OneOrMoreRowsHaveAMissingReasonType'));
          return;
        }

        // Check for duplicate reason names (case-insensitive)
        const names = gridData.map(r => r.Name?.toLowerCase().trim());
        const duplicateSet = new Set();
        names.forEach((name, index) => {
          if (name && names.indexOf(name) !== index) {
            const originalName = gridData.find(r => r.Name?.toLowerCase().trim() === name)?.Name;
            if (originalName) duplicateSet.add(originalName);
          }
        });

        if (duplicateSet.size > 0) {
          const duplicateList = [...duplicateSet].join(', ');
          onWarning(`${text('ReasonNamesAlreadyExist')}: ${duplicateList}`);
          return;
        }
      }

      try {
        loader(true)
        await Reasons.SaveReasons([...gridData, ...deleted.map(x => ({ ...x, Id: -x.Id }))])
        setDeleted([])
        onSuccess()
      } catch (error) {
        onError(error)
      } finally {
        loader(false)
      }
    })()
  };

  useEffect(() => {
    if (window.optima?.commands) {
      window.optima.commands.unsubscribe(MenuActions.save)
      window.optima.commands.subscribe(MenuActions.save, save);
    }
  }, [gridData, deleted])

  const reasonTypeFormatterToShow = ({ row }) => {
    return (<ReasonTypesDropDown
      clearable
      fluid
      placeholder={text('ChooseReasonType')}
      value={row.ReasonTypeId}
      onChange={(val) => handleFieldChange(row, 'ReasonTypeId', val)}
      reasonTypes={reasonTypesFromApi} />
    );
  };

  const isActiveFormatter = ({ row }) => {
    return (
      <Checkbox
        checked={row.IsActive}
        onChange={(val) => {
          handleFieldChange(row, 'IsActive', val);
        }} />
    );
  };

  const createTextFormatter = (property, maxLength) => {
    return ({ row }) => {
      return (
        <TextInput
          value={row[property]}
          onBlur={(val) => handleFieldChange(row, property, val)}
          maxLength={maxLength}
          style={{ width: '100%' }}
        />
      );
    };
  };

  const reasonTypeFormatter = ({ value }) => {
    const reasonType = reasonTypesFromApi.find(rt => rt.Id === value);
    return reasonType ? reasonType.Name : '';
  };

  const gridOptions = {
    search: {
      api: Reasons.GetReasons,
      searchOnLoad: true,
      fields: [
        <Form.Field
          key='ReasonTypeId'
          label={text('ReasonType')}
          control={ReasonTypesDropDown}
          clearable
          placeholder='All'
          reasonTypes={reasonTypesFromApi}
        />,
        <CheckboxWrapper
          label={text('IncludeInactive')}
          key='InActive'
        />,
      ]
    },
    grid: {
      columns: [
        {
          name: 'ReasonTypeId',
          title: text('ReasonType'),
          formatter: reasonTypeFormatter
        },
        {
          name: 'ReasonTypeIdToShow',
          title: text('ReasonType'),
          formatter: reasonTypeFormatterToShow,
        },
        {
          name: 'Name',
          title: text('Name'),
          formatter: createTextFormatter('Name', 100),
        },
        {
          name: 'Description',
          title: text('Description'),
          formatter: createTextFormatter('Description', 1000),
        },
        {
          name: 'IsActive',
          title: text('Active'),
          formatter: isActiveFormatter,
        }
      ],
      editable: {
        allowAdd: true,
        allowDelete: true,
        extraProps: {
          addedRows: [],
          onAddedRowsChange: addedRows => {
            setGridData(prev => [...prev, ...addedRows.map(row => ({ ...row, TempId: `newReasonId-${crypto.randomUUID()}`, IsActive: true }))]);
          }
        }
      },
      filtering: {
        extensions: [
          { columnName: 'ReasonTypeIdToShow', filteringEnabled: false },
          { columnName: 'IsActive', filteringEnabled: false },
        ],
      },
      showFilter: false,
      showDeleteMultiple: true,
      selectable: true,
      customOptions: {
        group: {
          grouping: [
            { columnName: 'ReasonTypeId' }
          ],
        }
      }
    }
  };

  return (
    <div style={{ flexDirection: 'column', height: '100%' }}>
      <GridSearchTemplate
        options={gridOptions}
        onGridUpdate={onGridUpdate}
        onSearchComplete={setGridData}
        data={gridData}
      />
    </div>
  );
};


export default ReasonsSettingPage;
